import { RegisterOptions } from 'react-hook-form';
import { OneWayInServiceUpsertDto } from 'src/api/v2';
import useServiceSubForm, { extractSubData, SubFormProps, SubFormReturn } from '../use-service-sub-form';
import { useTranslation } from 'react-i18next';

export type TargetFields =
  | 'companyAge'
  | 'companySize'
  | 'districtPolicyArea'
  | 'counties'
  | 'municipalities'
  | 'innovationLevel';
export type TargetData = Pick<OneWayInServiceUpsertDto, TargetFields>;

const useTargetForm = ({
  data,
  updateData,
  errors,
  setErrors,
}: SubFormProps<TargetData>): SubFormReturn<TargetData, TargetFields> & {
  validateIf: (
    condition: () => boolean,
    rule: any,
  ) => {
    validate: (value: any) => any;
  };
} => {
  const { t } = useTranslation('services');

  const extractTargetFields = (data: OneWayInServiceUpsertDto): TargetData => {
    return extractSubData(data, [
      'companyAge',
      'companySize',
      'districtPolicyArea',
      'counties',
      'municipalities',
      'innovationLevel',
    ]);
  };

  const validateIf = (condition: () => boolean, rule: RegisterOptions<Partial<TargetData>>) => {
    const originalValidate = rule.validate;
    return {
      validate: (value) => {
        if (condition() && typeof originalValidate === 'function') {
          return originalValidate(value);
        }

        return true;
      },
    };
  };

  const targetFields = extractTargetFields(data);
  const { form } = useServiceSubForm<TargetData>(targetFields, updateData, errors, setErrors);

  const rules: Partial<Record<keyof OneWayInServiceUpsertDto, any>> = {
    companyAge: { required: t('target.companyAge.required') },
    companySize: { required: t('target.companySize.required') },
    districtPolicyArea: {
      validate: (value: boolean | null | undefined) =>
        value === null || value === undefined ? t('target.districtPolicy.required') : true,
    },
    innovationLevel: { required: t('target.innovationLevelCriteria.required') },
    counties: {
      validate: () => {
        const counties = form.getValues('counties') || [];
        const municipalities = form.getValues('municipalities') || [];
        if (counties.length === 0 && municipalities.length === 0) {
          return t('target.geographic.required');
        }
        return true;
      },
    },
  };

  const registerWithRules = (field: TargetFields) => form.register(field, rules[field]);
  return { form, validateIf, rules, registerWithRules };
};

export default useTargetForm;
